import { BrowserLogger, LogLevel, ConsoleTransport } from '@soteria/logger/browser';

import { config } from '../../config';

import { getCurrentLevel, onLevelChange, LogLevel as UILogLevel } from './logLevelManager';

const appVersion = import.meta.env.VITE_APP_VERSION || '0.1.0';
const environment = config.env.mode || 'development';
const appInsightsConnectionString = config.monitoring.applicationInsights || '';

// Map UI log levels to logger package log levels
function mapUILogLevelToLoggerLevel(uiLevel: UILogLevel): LogLevel {
  switch (uiLevel) {
    case UILogLevel.ERROR:
      return LogLevel.ERROR;
    case UILogLevel.WARN:
      return LogLevel.WARN;
    case UILogLevel.INFO:
      return LogLevel.INFO;
    case UILogLevel.DEBUG:
      return LogLevel.DEBUG;
    case UILogLevel.TRACE:
      return LogLevel.TRACE;
    default:
      return LogLevel.INFO;
  }
}

// Create logger instance with dynamic log level from logLevelManager
export const logger = new BrowserLogger({
  appName: 'soteria-ui',
  appVersion,
  environment,
  // Use the current log level from the log level manager
  minLevel: mapUILogLevelToLoggerLevel(getCurrentLevel()),
  enableSampling: environment === 'production',
  // Reduced sampling rate in production to minimize overhead
  samplingRate: environment === 'production' ? 0.05 : 0.1,
  sensitiveFields: ['password', 'token', 'secret', 'authorization', 'accessToken'],
});

// Listen for log level changes and update the logger
onLevelChange(config => {
  const newLogLevel = mapUILogLevelToLoggerLevel(config.level);

  // Update the logger's minLevel option directly
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (logger as any).options.minLevel = newLogLevel;

  logger.info('Logger level updated', {
    newLevel: LogLevel[newLogLevel],
    source: config.source,
  });
});

// Add console transport
logger.addTransport(
  new ConsoleTransport({
    useColors: environment !== 'production',
    includeTimestamps: true,
    includeCorrelationId: true,
  })
);

// Add Application Insights transport if connection string is available
if (appInsightsConnectionString && appInsightsConnectionString.trim() !== '' && typeof window !== 'undefined') {
  // Dynamically import the browser App Insights transport
  import('@soteria/logger/browser')
    .then(module => {
      const { BrowserAppInsightsTransport } = module;

      logger.addTransport(
        new BrowserAppInsightsTransport(appInsightsConnectionString, {
          sensitiveFields: ['password', 'token', 'secret', 'authorization', 'accessToken'],
          samplingPercentage: environment === 'production' ? 10 : 100,
        })
      );

      logger.debug('Application Insights transport initialized', {
        environment,
        connectionString: `${appInsightsConnectionString.substring(0, 20)}...`,
      });
    })
    .catch(error => {
      logger.error('Failed to initialize Application Insights transport', { error });
    });
} else {
  // Log debug message when App Insights is skipped
  logger.debug('Application Insights transport skipped', {
    environment,
    reason: !appInsightsConnectionString || appInsightsConnectionString.trim() === '' ? 'No connection string provided' : 'Not in browser environment',
    hasConnectionString: !!appInsightsConnectionString && appInsightsConnectionString.trim() !== '',
    isBrowser: typeof window !== 'undefined',
  });
}

export * from './LoggerProvider';
export * from './apolloIntegration';
export * from './reduxMiddleware';
export * from './reduxConfig';
export * from './reduxHelpers';
export * from './reduxHooks';
export * from './appInsightsIntegration';
export * from './errorTracking';
export * from './globalErrorHandler';
export * from './ErrorBoundary';
